<div class="container-fluid py-3">
  <div class="content-info tin-page">

    <!-- ── Sticky document header ─────────────────────────────────────── -->
    <div class="zf-doc-head">
      <div class="zf-doc-left">
        <a class="zf-doc-back" href="#/transfer-in"><i class="bi bi-arrow-left"></i> Transfer In</a>
        <div class="zf-doc-title-row">
          <h3 class="zf-doc-title">{{ viewOnly ? 'Transfer Receipt Note' : 'Receive Consignment' }}</h3>
          @if (transferInNumber) {
          <span class="zf-doc-no">{{ transferInNumber }}</span>
          }
          @if (!viewOnly) {
          <span class="zf-status dot" [class.ok]="transferInCreated" [class.warn]="!transferInCreated">
            {{ transferInCreated ? 'Draft saved' : 'Awaiting receipt' }}
          </span>
          } @else {
          <span class="zf-status dot">Read only</span>
          <span class="zf-doc-note">Receipt note · no further edits possible</span>
          }
        </div>
      </div>

      @if (!viewOnly) {
      <div class="zf-doc-actions">
        <a class="btn btn-secondary" href="#/transfer-in">Cancel</a>
        @if (!hideDraftBtn) {
        <button type="button" class="btn btn-secondary" (click)="saveTransferIn()"
          [disabled]="transferInCreated || !lineCount">
          <i class="bi bi-save"></i> Save draft
        </button>
        }
        @if (transferInCreated || hideDraftBtn) {
        <button type="button" class="btn zf-btn-hero" (click)="finalizeTransferIn()" [disabled]="!lineCount">
          <i class="bi bi-check2-circle"></i> Finalize &amp; update stock
        </button>
        }
      </div>
      }
    </div>

    <!-- ── Consignment header: which shipment this receipt is against ─── -->
    <div class="zf-doc-section">
      <div class="zf-section-head collapsible" [class.closed]="!headerOpen" (click)="headerOpen = !headerOpen">
        <span class="zf-section-title">Consignment</span>
        <div class="zf-sum-chips">
          <span class="zf-sum-chip">
            <span class="k">TO</span><span class="mono">{{ transferOut?.transferOutNumber || '—' }}</span>
          </span>
          <span class="zf-sum-chip"><span class="k">From</span>{{ transferOut?.sourceLocation?.name || '—' }}</span>
          <span class="zf-sum-chip"><span class="k">To</span>{{ transferOut?.targetLocation?.name || '—' }}</span>
          @if (lineCount) {
          <span class="zf-sum-chip primary">{{ lineCount }} {{ lineCount === 1 ? 'line' : 'lines' }}</span>
          }
        </div>
        <span class="zf-section-toggle">
          {{ headerOpen ? 'Collapse header' : 'Show header' }}
          <i class="bi" [class.bi-chevron-up]="headerOpen" [class.bi-chevron-down]="!headerOpen"></i>
        </span>
      </div>

      @if (headerOpen) {
      <div class="zf-section-body zf-stack">
        <div class="zf-fgrid" style="--cols: repeat(4, minmax(0,1fr))">
          <div class="zf-field">
            <span class="zf-flabel">Transfer out no <span class="meta">· source document</span></span>
            <input class="form-control zf-mono" [value]="transferOut?.transferOutNumber || ''" readonly />
          </div>
          <div class="zf-field">
            <span class="zf-flabel">Transfer in no</span>
            <input class="form-control zf-mono" [value]="transferInNumber || ''" readonly />
          </div>
          <div class="zf-field">
            <span class="zf-flabel">From</span>
            <input class="form-control" [value]="transferOut?.sourceLocation?.name || ''" readonly />
          </div>
          <div class="zf-field">
            <span class="zf-flabel">To</span>
            <input class="form-control" [value]="transferOut?.targetLocation?.name || ''" readonly />
          </div>
        </div>

        <div class="zf-hr"></div>

        <div class="zf-fgrid" style="--cols: minmax(0,1fr)">
          <div class="zf-field">
            <span class="zf-flabel">Remarks <span class="meta">· carried from the transfer out</span></span>
            <input class="form-control" [value]="transferOut?.remarks || 'N/A'" readonly />
          </div>
        </div>
      </div>
      }
    </div>

    <!-- ── Lines received ─────────────────────────────────────────────── -->
    <div class="zf-doc-section zf-lines-card">
      <div class="zf-lines-head">
        <h2>Lines received</h2>
        <span class="zf-count">{{ lineCountLabel }}</span>
        <span class="zf-units">{{ totalQuantity | number : '1.0-3' }} units</span>
        <div class="zf-lines-tools">
          <label class="zf-search" style="width:200px">
            <i class="bi bi-search"></i>
            <input [value]="lineQuery" (input)="lineQuery = $any($event.target).value" placeholder="Filter these lines" />
          </label>
        </div>
      </div>

      <div class="zf-scoped">
        <span class="k">Receipt</span>
        @if (viewOnly) {
        <span>Quantities and cost carried from <strong>{{ transferOut?.sourceLocation?.name || 'the source location' }}</strong></span>
        } @else {
        <span>Quantities are received exactly as sent from <strong>{{ transferOut?.sourceLocation?.name || 'the source location' }}</strong></span>
        }
        <span class="hint">Rows locked left · scroll right for value</span>
      </div>

      @if (lineCount) {
      <div class="zf-sheet-wrap">
        <table class="zf-sheet" aria-label="Transfer in lines">
          <thead>
            <tr>
              <th class="st" style="--l:0; width:42px">#</th>
              <th class="st" style="--l:42px; width:96px">SKU</th>
              <th class="st st-end" style="--l:138px">Description</th>
              <th class="num" style="width:92px">Sent</th>
              <th class="num" style="width:96px">Received</th>
              <th class="num" style="width:96px">MRP</th>
              <th class="num" style="width:104px">Cost price</th>
              <th class="num" style="width:120px">Value</th>
            </tr>
          </thead>
          <tbody>
            @for (row of visibleLines; track row.item) {
            <tr>
              <td class="st zf-idx" style="--l:0">{{ row.index + 1 }}</td>
              <td class="st" style="--l:42px"><span class="zf-sku">{{ row.item.productVariant.product.sku }}</span></td>
              <td class="st st-end zf-name" style="--l:138px">
                <span class="n" [title]="row.item.productVariant.variantName">{{ row.item.productVariant.variantName }}</span>
                <span class="s">{{ row.item.productVariant.product?.uom || 'AS SENT' }}</span>
              </td>
              <td class="num dim">{{ row.item.quantity | number : "1.0-3" }}</td>
              <td class="num strong">{{ (row.item.quantityReceived || row.item.quantity) | number : "1.0-3" }}</td>
              <td class="num ink3">{{ row.item.productVariant.pricing[0]?.mrp || "-" }}</td>
              <td class="num ink3">{{ row.item.productVariant.pricing[0]?.costPrice || "-" }}</td>
              <td class="num strong">{{ lineValue(row.item) | number : '1.2-2' }}</td>
            </tr>
            }
            @if (!visibleLines.length) {
            <tr>
              <td colspan="8" class="zf-sheet-empty">
                <div>
                  <span class="t">No lines match this filter</span>
                  <span class="s">Clear the filter to see all {{ lineCount }} lines.</span>
                </div>
              </td>
            </tr>
            }
          </tbody>
          <tfoot>
            <tr>
              <td class="st" colspan="3" style="--l:0"><span class="k">TOTAL</span>{{ lineCountLabel }}</td>
              <td class="num dim">{{ totalSentQty | number : '1.0-3' }}</td>
              <td class="num">{{ totalQuantity | number : '1.0-3' }}</td>
              <td></td>
              <td class="num dim">Grand total</td>
              <td class="num strong">{{ grandTotal | currency : "INR" }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      } @else {
      <div class="zf-sheet-blank">
        <span class="t">No lines on this consignment</span>
        <span class="s">The source transfer out has no items to receive.</span>
      </div>
      }
    </div>

    <!-- ── Receipt value strip ────────────────────────────────────────── -->
    <div class="zf-order-strip">
      <div class="zf-strip-info">
        <div class="zf-strip-line">
          <span class="dot" [class.ok]="lineCount"></span>
          {{ lineCountLabel }} · {{ totalQuantity | number : '1.0-3' }} units
        </div>
        <div class="zf-strip-sub">
          @if (viewOnly) {
          <span>Stock was added at {{ transferOut?.targetLocation?.name || 'the destination' }} when this receipt was finalized.</span>
          } @else if (!transferInCreated) {
          <span>Save a draft first; finalizing adds the stock at {{ transferOut?.targetLocation?.name || 'the destination' }} and cannot be reversed.</span>
          } @else {
          <span>Draft saved · finalize to add the stock at {{ transferOut?.targetLocation?.name || 'the destination' }}.</span>
          }
        </div>
      </div>
      <div class="zf-strip-kpis">
        <div class="zf-kpi">
          <span class="k">Lines</span>
          <span class="v">{{ lineCount }}</span>
        </div>
        <div class="zf-kpi">
          <span class="k">Units</span>
          <span class="v">{{ totalQuantity | number : '1.0-3' }}</span>
        </div>
        <div class="zf-kpi dark">
          <span class="k">Receipt value · at cost</span>
          <span class="v">{{ grandTotal | currency : "INR" }}</span>
        </div>
      </div>
    </div>

  </div>
</div>
